import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalShell from "../../pages/Templates/ModalShell";
import Skeleton from "../Skeleton/Skeleton";
import { commitPreview, commitRun } from "../../services/timetableGenerationService";
import { codeMessage, runErrorMessage } from "./runErrors";
import { WEEKDAY_KEY } from "../../utils/helpers/timetableDisplay";

// Commit is the one destructive action in the whole timetabling feature: it
// REPLACES the school's entire active grid, hand-entered and CSV-imported rows
// included. So the dialog never opens straight onto a confirm button — it loads
// commit-preview first and shows what disappears, and when hand-authored rows
// are among the casualties the confirm stays disabled behind an explicit
// checkbox.
//
// Built on ModalShell rather than ConfirmModal because this is a two-stage flow
// (preview -> result): after committing we keep the dialog open to report
// `skipped` rows, which ConfirmModal's fixed Cancel/Confirm footer cannot show.


// Order the source breakdown so the destructive rows read first.
const SOURCE_ORDER = ["manual", "imported", "generated"];

function StatTile({ label, value, tone = "slate", hint }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    amber: "bg-amber-50 border-amber-100 text-amber-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
  };
  return (
    <div className={`rounded-2xl border px-3 py-3 ${tones[tone] || tones.slate}`}>
      <p className="text-[9px] font-black uppercase tracking-widest opacity-70">{label}</p>
      <p className="text-2xl font-black tabular-nums leading-tight mt-0.5">{value}</p>
      {hint && <p className="text-[10px] font-bold opacity-70 mt-0.5 leading-snug">{hint}</p>}
    </div>
  );
}

// Same idea as StatTile above: one tone map, so the four alerts in this dialog
// can't drift apart the way their hand-copied class strings already had.
function Alert({ tone = "rose", children }) {
  const tones = {
    rose: "text-rose-600 bg-rose-50 border-rose-100",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
  };
  return (
    <p
      role="alert"
      className={`text-[11px] font-black border rounded-xl px-3 py-2 leading-relaxed ${
        tones[tone] || tones.rose
      }`}
    >
      {children}
    </p>
  );
}

// The fields the confirm gate is decided on. Committing re-reads the preview and
// compares this — one string, so adding a fifth gating field can't be applied to
// the comparison and forgotten in the dependency array.
const gateOf = (p) =>
  [
    p?.hand_authored_at_risk || 0,
    Boolean(p?.would_empty_grid),
    Boolean(p?.reviewable),
  ].join("|");

export default function CommitDialog({ runUuid, onClose, onCommitted }) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [acknowledged, setAcknowledged] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [result, setResult] = useState(null);
  //: The grid changed under us between opening the dialog and confirming.
  const [staleNotice, setStaleNotice] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError("");
    commitPreview({ uuid: runUuid, signal: controller.signal })
      .then((data) => setPreview(data))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setLoadError(runErrorMessage(err, t, t("timetable.previewFailed")));
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [runUuid, t]);

  const atRisk = preview?.hand_authored_at_risk || 0;
  const needsAck = atRisk > 0;
  // A board with nothing on it would delete the school's timetable and write
  // nothing back. That is never what the manager meant, so it is a hard block,
  // not an acknowledgeable warning — the backend refuses it too.
  const wouldEmpty = Boolean(preview?.would_empty_grid);
  const canCommit =
    Boolean(preview?.reviewable) &&
    !wouldEmpty &&
    (!needsAck || acknowledged) &&
    !isCommitting &&
    !result;

  const onConfirm = useCallback(async () => {
    setIsCommitting(true);
    setStaleNotice(false);
    try {
      // Re-read the preview immediately before the irreversible write. The
      // numbers on screen were taken when the dialog opened and are what the
      // acknowledgement gate was decided on — a colleague importing 400 rows
      // in the meantime would otherwise have them destroyed silently, because
      // `hand_authored_at_risk` was 0 when the checkbox was skipped.
      const fresh = await commitPreview({ uuid: runUuid });
      if (gateOf(fresh) !== gateOf(preview)) {
        setPreview(fresh);
        setAcknowledged(false);
        setStaleNotice(true);
        return;
      }
      const data = await commitRun({ uuid: runUuid });
      setResult(data);
      if (onCommitted) onCommitted(data);
    } catch (err) {
      setLoadError(runErrorMessage(err, t, t("timetable.commitFailed")));
    } finally {
      setIsCommitting(false);
    }
  }, [runUuid, onCommitted, t, preview]);

  const bySource = preview?.will_remove_by_source || {};
  const sourceRows = [
    ...SOURCE_ORDER.filter((key) => bySource[key]),
    ...Object.keys(bySource).filter((key) => !SOURCE_ORDER.includes(key) && bySource[key]),
  ];

  return (
    <ModalShell onClose={onClose} busy={isCommitting} maxWidth="max-w-lg">
      <h3 className="text-lg font-bold text-slate-900 pr-8">
        {result ? t("timetable.commitDoneTitle") : t("timetable.commitTitle")}
      </h3>

      {!result && (
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t("timetable.commitBody")}</p>
      )}

      <div className="mt-5 max-h-[55vh] overflow-y-auto -mx-1 px-1">
        {isLoading && (
          <div className="space-y-3" aria-busy="true">
            <div className="grid grid-cols-2 gap-3">
              <Skeleton className="h-20 rounded-2xl" />
              <Skeleton className="h-20 rounded-2xl" />
              <Skeleton className="h-20 rounded-2xl" />
              <Skeleton className="h-20 rounded-2xl" />
            </div>
            <Skeleton className="h-12 rounded-2xl" />
          </div>
        )}

        {!isLoading && loadError && !result && (
          <Alert>{loadError}</Alert>
        )}

        {!isLoading && preview && !result && (
          <div className="space-y-4">
            {!preview.reviewable && (
              <Alert>{codeMessage("not_reviewable", t)}</Alert>
            )}

            {preview.reviewable && wouldEmpty && (
              <Alert>{codeMessage("empty_board", t)}</Alert>
            )}

            {staleNotice && (
              <Alert tone="amber">{t("timetable.previewChanged")}</Alert>
            )}

            <div className="grid grid-cols-2 gap-3">
              <StatTile
                label={t("timetable.willWriteCards")}
                value={preview.will_write_cards ?? 0}
                tone="emerald"
              />
              <StatTile label={t("timetable.willRemove")} value={preview.will_remove ?? 0} />
              <StatTile
                label={t("timetable.handAuthoredAtRisk")}
                value={atRisk}
                tone={atRisk > 0 ? "rose" : "slate"}
                hint={atRisk > 0 ? t("timetable.handAuthoredHint") : undefined}
              />
              <StatTile
                label={t("timetable.cardsInTrayLabel")}
                value={preview.cards_in_tray ?? 0}
                tone={preview.cards_in_tray > 0 ? "amber" : "slate"}
                hint={preview.cards_in_tray > 0 ? t("timetable.cardsInTrayHint") : undefined}
              />
            </div>

            {sourceRows.length > 0 && (
              <div className="rounded-2xl border border-slate-200 overflow-hidden">
                <p className="px-4 py-2 bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {t("timetable.removedBySource")}
                </p>
                <ul className="divide-y divide-slate-100">
                  {sourceRows.map((key) => (
                    <li key={key} className="px-4 py-2 flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-600">
                        {t(`timetable.source.${key}`, key)}
                      </span>
                      <span className="text-xs font-black tabular-nums text-slate-900">
                        {bySource[key]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {needsAck && (
              <label className="flex items-start gap-3 rounded-2xl bg-rose-50 border border-rose-200 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-rose-300 text-rose-600 focus:ring-rose-500"
                />
                <span className="text-xs font-bold text-rose-700 leading-relaxed">
                  {t("timetable.acknowledgeDestroy", { count: atRisk })}
                </span>
              </label>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <StatTile label={t("timetable.written")} value={result.written ?? 0} tone="emerald" />
              <StatTile label={t("timetable.removed")} value={result.removed ?? 0} />
            </div>

            {Array.isArray(result.skipped) && result.skipped.length > 0 ? (
              <div className="rounded-2xl border border-amber-200 overflow-hidden">
                <div className="px-4 py-3 bg-amber-50">
                  <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">
                    {t("timetable.skippedTitle", { count: result.skipped.length })}
                  </p>
                  <p className="mt-1 text-[11px] font-medium text-amber-700/80 leading-relaxed">
                    {t("timetable.skippedHint")}
                  </p>
                </div>
                <ul className="divide-y divide-amber-100">
                  {result.skipped.map((row, index) => (
                    <li key={`${row.group}-${row.weekday}-${row.period}-${index}`} className="px-4 py-2.5">
                      <p className="text-xs font-black text-slate-800">
                        {row.group} · {t(`timetable.weekday.${WEEKDAY_KEY[row.weekday] || "sun"}`)} ·{" "}
                        {t("timetable.periodShort", { period: row.period })}
                      </p>
                      <p className="text-[11px] font-medium text-slate-500 mt-0.5">
                        {codeMessage(row.code, t) || row.message}
                      </p>
                      {row.teacher && (
                        <p className="text-[10px] font-bold text-slate-500 mt-0.5">{row.teacher}</p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3">
                {t("timetable.commitClean")}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={isCommitting}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
        >
          {result ? t("timetable.done") : t("common.cancel")}
        </button>
        {!result && (
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canCommit}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {isCommitting ? t("timetable.committing") : t("timetable.commitConfirm")}
          </button>
        )}
      </div>
    </ModalShell>
  );
}
